import { useFormikContext } from "formik";

import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { FormGroup } from "@vega-modules/scenes/CoInvestmentsGP/components/CoInvestmentEditForm/components/FormGroup";

import { type CoInvestmentEditFormData } from "../CoInvestmentEditForm";

import { MODAL_TYPES } from "./constants/modalConstants";
import { useDealOverviewActions } from "./hooks/useDealOverviewActions";
import { AddMetricSectionModal } from "./AddMetricSectionModal";
import { DealOverviewSection } from "./DealOverviewSection";

const MAX_METRICS = 3;

const MAX_SECTIONS = 5;

type LpDealOverviewContentSectionProps = {
  editable?: boolean;
};

export const LpDealOverviewContentSection = ({
  editable,
}: LpDealOverviewContentSectionProps) => {
  const { values } = useFormikContext<CoInvestmentEditFormData>();
  const dealCurrency = values.currency;

  const {
    addItem,
    removeItem,
    getExistingNames,
    getItems,
    isRemovingMetric,
    isRemovingSection,
  } = useDealOverviewActions();

  const {
    value: isMetricModalOpen,
    setTrue: openMetricModal,
    setFalse: closeMetricModal,
  } = useBoolean(false);
  const {
    value: isSectionModalOpen,
    setTrue: openSectionModal,
    setFalse: closeSectionModal,
  } = useBoolean(false);

  return (
    <>
      <FormGroup
        dataTestId="lp-deal-overview-content"
        label={m.coInvestments_lpDealOverviewContent_title()}
      >
        <div className="space-y-4">
          <DealOverviewSection
            buttonLabel={m.coInvestments_lpDealOverviewContent_add({
              type: MODAL_TYPES.METRIC,
            })}
            dealCurrency={dealCurrency}
            description={
              editable
                ? m.coInvestments_lpDealOverviewContent_addUpToThreeMetrics()
                : ""
            }
            editable={editable}
            getFieldName={(index, field) =>
              `dealOverview.metrics.${index}.${field}`
            }
            isAddDisabled={getItems("metrics").length >= MAX_METRICS}
            isRemoving={isRemovingMetric}
            items={getItems("metrics")}
            onAdd={openMetricModal}
            onRemove={(name) => removeItem("metrics", name)}
            sectionType={MODAL_TYPES.METRIC}
            title={m.coInvestments_lpDealOverviewContent_keyMetrics()}
          />

          <DealOverviewSection
            buttonLabel={m.coInvestments_lpDealOverviewContent_add({
              type: MODAL_TYPES.SECTION,
            })}
            description={
              editable
                ? m.coInvestments_lpDealOverviewContent_addUpToFiveSections()
                : ""
            }
            editable={editable}
            getFieldName={(index, field) =>
              `dealOverview.sections.${index}.${field}`
            }
            isAddDisabled={getItems("sections").length >= MAX_SECTIONS}
            isRemoving={isRemovingSection}
            items={getItems("sections")}
            onAdd={openSectionModal}
            onRemove={(name) => removeItem("sections", name)}
            sectionType={MODAL_TYPES.SECTION}
            title={m.coInvestments_lpDealOverviewContent_sections()}
          />
        </div>
      </FormGroup>

      <AddMetricSectionModal
        dealCurrency={dealCurrency}
        existingNames={getExistingNames("metrics")}
        isOpen={isMetricModalOpen}
        modalType={MODAL_TYPES.METRIC}
        onAdd={addItem}
        onClose={closeMetricModal}
      />

      <AddMetricSectionModal
        existingNames={getExistingNames("sections")}
        isOpen={isSectionModalOpen}
        modalType={MODAL_TYPES.SECTION}
        onAdd={addItem}
        onClose={closeSectionModal}
      />
    </>
  );
};
